import { Box, Card, CardActionArea, CardContent, Stack, Typography } from '@mui/material';
import { PlayCircleOutline, TrendingFlat } from '@mui/icons-material';
import { whatsNewMediaUrl } from 'api/app/whats-new-media.api';
import { IFeature, isAiFeature } from '../data/types';
import { colors, glowPulse, glowShift, reducedMotion } from '../styles';
import AiSideTag from './AiSideTag';
import FeatureMedia from './FeatureMedia';
import PricingChip from './PricingChip';
import GoalPathChips from './GoalPathChips';
import FeaturePromoChips from './FeaturePromoChips';

interface FeatureCardProps {
    feature: IFeature;
    /** `spotlight` — כרטיס גדול עם הילה זוהרת. `catalog` — כרטיס רגיל בגריד */
    variant: 'spotlight' | 'catalog';
    onOpen: (feature: IFeature) => void;
}

const statusChipSx = (kind: 'soon' | 'pilot' | 'new') => {
    if (kind === 'soon') {
        return {
            color: '#ffffff',
            bgcolor: '#dc2626',
            boxShadow: '0 8px 20px rgba(220, 38, 38, 0.4)'
        };
    }
    if (kind === 'pilot') {
        return {
            color: '#3b1d73',
            bgcolor: '#e4d4ff',
            boxShadow: '0 8px 20px rgba(124, 77, 255, 0.35)'
        };
    }
    return {
        color: '#09243a',
        bgcolor: '#b9f7ff',
        boxShadow: '0 8px 20px rgba(34, 211, 238, 0.45)'
    };
};

const FeatureCard = ({ feature, variant, onOpen }: FeatureCardProps) => {
    const spotlight = variant === 'spotlight';
    const unavailable = Boolean(feature.comingSoon);
    const hasVideo = Boolean(feature.videoFile);
    const ai = isAiFeature(feature);
    const statusChips: { label: string; kind: 'soon' | 'pilot' | 'new' }[] = [];
    if (feature.comingSoon) statusChips.push({ label: 'בקרוב', kind: 'soon' });
    if (feature.pilot) statusChips.push({ label: 'בפיילוט', kind: 'pilot' });
    if (spotlight && statusChips.length === 0) statusChips.push({ label: 'חדש', kind: 'new' });

    const card = (
        <Card
            dir="rtl"
            sx={{
                position: 'relative',
                height: '100%',
                borderRadius: '22px',
                overflow: 'hidden',
                bgcolor: unavailable ? '#eef0f2' : undefined,
                boxShadow: unavailable
                    ? '0 10px 28px rgba(55, 65, 81, 0.12)'
                    : spotlight
                      ? 'none'
                      : '0 18px 45px rgba(15, 50, 80, 0.14)',
                transition: 'transform 180ms ease, box-shadow 180ms ease',
                '&:hover': unavailable
                    ? { transform: 'none', boxShadow: '0 10px 28px rgba(55, 65, 81, 0.12)' }
                    : {
                          transform: 'translateY(-6px)',
                          boxShadow: spotlight ? 'none' : '0 26px 70px rgba(15, 50, 80, 0.24)'
                      },
                [reducedMotion]: { '&:hover': { transform: 'none' } }
            }}
        >
            {ai && <AiSideTag muted={unavailable} />}
            <CardActionArea
                onClick={() => onOpen(feature)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
            >
                <Box sx={{ position: 'relative' }}>
                    <FeatureMedia
                        src={whatsNewMediaUrl(feature.imageFile)}
                        alt={feature.title}
                        badge={feature.badge === 'AI' ? undefined : feature.badge}
                        badgeInsetEnd={ai ? 46 : 16}
                        height={spotlight ? { xs: 220, md: 280 } : { xs: 180, md: 200 }}
                        showPlayOverlay={hasVideo && !unavailable}
                        muted={unavailable}
                    />
                    {statusChips.length > 0 && (
                        <Stack
                            spacing={0.75}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                insetInlineStart: 16
                            }}
                        >
                            {statusChips.map((chip) => (
                                <Box
                                    key={chip.label}
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '999px',
                                        fontWeight: 900,
                                        fontSize: 13,
                                        width: 'fit-content',
                                        ...statusChipSx(chip.kind)
                                    }}
                                >
                                    {chip.label}
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Box>

                <CardContent
                    sx={{
                        p: spotlight ? 3 : 2.5,
                        width: '100%',
                        flexGrow: 1,
                        pe: ai ? 5 : undefined
                    }}
                >
                    <Stack spacing={1.5} sx={{ height: '100%' }} dir="rtl">
                        <Typography
                            variant={spotlight ? 'h3' : 'h4'}
                            dir="rtl"
                            sx={{
                                fontWeight: 900,
                                color: unavailable ? '#6b7280' : colors.ink,
                                lineHeight: 1.35,
                                textAlign: 'start'
                            }}
                        >
                            {feature.title}
                        </Typography>
                        <Typography
                            sx={{
                                color: unavailable ? '#9ca3af' : colors.body,
                                lineHeight: 1.7,
                                flexGrow: 1,
                                textAlign: 'start'
                            }}
                        >
                            {feature.shortDescription}
                        </Typography>
                        {feature.goalPath && (
                            <Box sx={{ opacity: unavailable ? 0.55 : 1, width: '100%' }}>
                                <GoalPathChips path={feature.goalPath} size="compact" />
                            </Box>
                        )}
                        {feature.promoChips && feature.promoChips.length > 0 && (
                            <Box sx={{ opacity: unavailable ? 0.55 : 1, width: '100%' }}>
                                <FeaturePromoChips chips={feature.promoChips} size="compact" />
                            </Box>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <PricingChip pricing={feature.pricing} />
                            {feature.pricing.note && (
                                <Typography sx={{ color: colors.muted, fontSize: 13 }}>{feature.pricing.note}</Typography>
                            )}
                        </Stack>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: unavailable ? '#9ca3af' : 'primary.main',
                                fontWeight: 800
                            }}
                        >
                            <Typography sx={{ fontWeight: 800 }}>
                                {unavailable ? 'בקרוב' : hasVideo ? 'לצפייה בהדגמה' : 'לראות פרטים'}
                            </Typography>
                            {!unavailable && (hasVideo ? <PlayCircleOutline /> : <TrendingFlat sx={{ transform: 'scaleX(-1)' }} />)}
                        </Box>
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );

    if (!spotlight || unavailable) return card;

    return (
        <Box
            sx={{
                p: '2px',
                borderRadius: '24px',
                background: 'linear-gradient(120deg, #22d3ee, #1264e2, #00a884, #22d3ee)',
                backgroundSize: '300% 300%',
                animation: `${glowShift} 6s ease infinite, ${glowPulse} 3.2s ease-in-out infinite`,
                [reducedMotion]: {
                    animation: 'none',
                    boxShadow: '0 18px 45px rgba(15, 50, 80, 0.18)'
                }
            }}
        >
            {card}
        </Box>
    );
};

export default FeatureCard;
